/**
 * This script provides all of the functionality to CEVA's 4 step online quoting & booking process.
 * It expects a particular markup format which can be currently found in the QuotePage.ss.
 *
 * This script depends on;
 * - jQuery
 * - jQuery Validation plugin - https://jqueryvalidation.org/
 * - jQuery UI (dialogue plugin) - https://jqueryui.com/dialog/
 */

/**
 * A namespace for the quoting/booking functionality.
 * @namespace
 */

export default () => {
    var Quote = {};
    Quote.$ = jQuery;

    /**
     * @prop {jQuery} The main form element. Set in Quote.init().
     */
    Quote.form = null;

    /**
     * @prop {string|null} The HTML string template used to generate vehicle elements. Set in Quote.init().
     */
    Quote.vehicleTemplate = null;

    /**
     * @prop {jQuery|null} The jQuery element that holds the genereated vehicle elements. Set in Quote.init().
     */
    Quote.vehiclesTargetEl = null;

    /**
     * @prop {jQuery|null} The jQuery element that holds the generated quote elements. Set in Quote.init().
     */
    Quote.quotesTargetEl = null;

    /**
     * @prop {jQuery|null} The jQuery element that holds no quote message. Set in Quote.init().
     */
    Quote.noQuoteMsgEl = null;

    /**
     * @prop {string|null} The HTML string template used to generated quotes elements. Set in Quote.init().
     */
    Quote.quoteTemplate = null;

    /**
     * @prop {number|null} The current step the user is on. Set in Quote.showStep().
     */
    Quote.currentStep = null;

    /**
     * @prop {boolean} Whether the user can navigate between steps. TRUE = enabled, FALSE = disabled.
     */
    Quote.stepNavigationStatus = true;

    /**
     * @prop {number} Keeps track of unique ids for the vehicle elements' form fields.
     */
    Quote.vehicleUniqueCount = 0;

    /**
     * @prop {object[]} An array of quote objects.
     */
    Quote.quotes = [];

    Quote.pickUpDepot = '';


    /**
     * @prop {string[]} An array of excluded pickup dates
     */
    Quote.excludedPickUpDates = null;


    Quote.submitBookingDone = false;


    Quote.datePickerDateExclusionCallback = function (date) {
        var excludedDates = Quote.getExcludedPickUpDates();

        var month = ('0' + (date.getMonth() + 1)).slice(-2);
        var dom = ('0' + date.getDate()).slice(-2);
        var year = date.getFullYear();

        var currDate = new Date;
        var crrMonth = ('0' + (currDate.getMonth() + 1)).slice(-2);
        var crrDom = ('0' + currDate.getDate()).slice(-2);
        var currYear = currDate.getFullYear();

        var dateSimple = year + "-" + month + "-" + dom;
        var currDateSimple = currYear + "-" + crrMonth + "-" + crrDom;

        for (var i = 0; i < excludedDates.length; i++) {
            var excluded = $.inArray(year + "-" + month + "-" + dom, excludedDates);
            if (excluded != -1 || (Quote.pickUpDepot && currDateSimple > dateSimple) || (!Quote.pickUpDepot && currDateSimple >= dateSimple)) {
                return [false, ""]
            }
        }
        if (Quote.pickUpDepot) {
            return dateSimple >= currDateSimple ? [true] : [false];
        } else {
            return dateSimple > currDateSimple ? [true] : [false];
        }

    };

    /**
     * @prop {object} Form validation rules that are applied to each step and the form as a whole. See https://jqueryvalidation.org/
     */
    Quote.validationRules = {
        'step1': {
            PickUpLocation: {'required': true, 'suburbStatePostcode': true},
            DeliveryLocation: {'required': true, 'suburbStatePostcode': true},
            ConfirmInfo: {'required': true}
        },
        'step2': {
            Quote: {'required': true},
            PONo: {'required': false}
        },
        'step3': {
            // PONo: {'required': true},
        },
        'step4a': {
            ContactTitle: 'required',
            ContactFirstName: {'required': true, 'alphaNum': true, 'emptySpaceOnly': true},
            ContactSurname: {'required': true, 'emptySpaceOnly': true},
            ContactEmail: {'email': true, 'required': true},
            ContactMobile: {
                'required': {
                    depends: function (el) {
                        return !Quote.form.find('.js-contactphone').val();
                    }
                },
                'digits': true,
                'minlength': 10,
                'maxlength': 10
            },
            ContactPhone: {
                'required': {
                    depends: function (el) {
                        return !Quote.form.find('.js-contactmobile').val();
                    }
                },
                'digits': true,
                'minlength': 10,
                'maxlength': 10
            }
        },
        'step4b': {
            PickUpContactTitle: 'required',
            PickUpContactFirstName: {'required': true, 'alphaNum': true, 'emptySpaceOnly': true},
            PickUpContactSurname: {'required': true, 'emptySpaceOnly': true},
            /*PickUpContactEmail: {'email': true, 'required': true},*/
            PickUpContactMobile: {
                'required': {
                    depends: function (el) {
                        return !Quote.form.find('.js-pickupcontactphone').val();
                    }
                },
                'digits': true,
                'minlength': 10,
                'maxlength': 10
            },
            PickUpContactPhone: {
                'required': {
                    depends: function (el) {
                        return !Quote.form.find('.js-pickupcontactmobile').val();
                    }
                },
                'digits': true,
                'minlength': 10,
                'maxlength': 10
            },
            PickUpDate: 'pickUpDateTime',
            // PickUpTime: 'required',
            PickUpType: 'required',
            PickUpAddress1: {
                'required': {
                    depends: function (el) {
                        return Quote.form.find('.js-pickuptype').val() == '1'
                    }
                },
                'emptySpaceOnly': {
                    depends: function (el) {
                        return Quote.form.find('.js-pickuptype').val() == '1'
                    }
                }
            },
            PickUpDepot: {
                'required': {
                    depends: function (el) {
                        return Quote.form.find('.js-pickuptype').val() == '0'
                    }
                }
            },
            PickUpSuburb: 'required',
            PickUpState: 'required',
            PickUpPostcode: 'required',
        },
        'step4c': {
            DeliveryContactTitle: 'required',
            DeliveryContactFirstName: {'required': true, 'alphaNum': true, 'emptySpaceOnly': true},
            DeliveryContactSurname: {'required': true, 'emptySpaceOnly': true},
            /*DeliveryContactEmail: {'email': true, 'required': true},*/
            DeliveryContactMobile: {
                'required': {
                    depends: function (el) {
                        return !Quote.form.find('.js-deliverycontactphone').val();
                    }
                },
                'digits': true,
                'minlength': 10,
                'maxlength': 10
            },
            DeliveryContactPhone: {
                'required': {
                    depends: function (el) {
                        return !Quote.form.find('.js-deliverycontactmobile').val();
                    }
                },
                'digits': true,
                'minlength': 10,
                'maxlength': 10
            },
            DeliveryType: 'required',
            DeliveryAddress1: {
                'required': {
                    depends: function (el) {
                        return Quote.form.find('.js-deliverytype').val() == '1'
                    }
                },
                'emptySpaceOnly': {
                    depends: function (el) {
                        return Quote.form.find('.js-deliverytype').val() == '1'
                    }
                }
            },
            DeliveryDepot: {
                'required': {
                    depends: function (el) {
                        return Quote.form.find('.js-deliverytype').val() == '0'
                    }
                }
            },
            DeliverySuburb: 'required',
            DeliveryState: 'required',
            DeliveryPostcode: 'required',
        },
        'step4d': {
            TermsOfUse: 'required'
        }
    };

    /**
     * @prop {object} The messages to show when a validation rule fails where the key is the field name and the value is message string.
     * This does NOT apply to vehicle fields. See Quote.vehicleValidationRules instead.
     */
    Quote.validationMessages = {
        TermsOfUse: 'You MUST agree to Terms &amp; Condition to proceed',
        Quote: 'You MUST select one of the quotes to proceed',
        ConfirmInfo: 'You MUST confirm that the information above are correct',
        ContactPhone: 'Please add area code followed by the number - No Spaces - Eg 0287072999',
        PickUpContactPhone: 'Please add area code followed by the number - No Spaces - Eg 0287072999',
        DeliveryContactPhone: 'Please add area code followed by the number - No Spaces - Eg 0287072999'
    };

    Quote.validationPlacements = function (error, el) {
        var quoteSelErrorEl = Quote.$('.js-quoteselectionerror');
        if (el.hasClass('js-quoteselection') && quoteSelErrorEl.length) {
            quoteSelErrorEl.append(error);
            return;
        }
        error.insertAfter(el);
    };

    /**
     * @prop {object} Form validation rules that are applied to each dynamic vehicle. See https://jqueryvalidation.org/
     */

    Quote.vehicleValidationRules = {
        'Vehicles[][MakeCode]': {
            'vehicleSpecifier': true
        },
        'Vehicles[][ModelCode]': {
            'vehicleSpecifier': true
        },
        'Vehicles[][TypeCode]': {
            'vehicleSpecifier': true
        },
        'Vehicles[][VehicleId]': {
            'required': true,
            'alphaNum': true
        },
        'Vehicles[][VehicleDriveable]': {
            'valueIsTrue': true,
            'messages': {
                'valueIsTrue': 'CEVA only accepts vehicles that are able to be driven via this online form. Please call our Customer Care Centre on 13 2277 for a personalised quote for non-driveable vehicles.'
            }
        },
        'Vehicles[][VehicleValue]': {
            'required': true,
            'alphaNum': true,
            'min': 60000,
            'cevaCarValueMax': true
        }
    };

    /**
     * @prop {object} Custom form validation methods.
     */
    Quote.validationMethods = {
        'reviewQuoteId': {
            message: 'It seems as though your quote is not valid',
            callback: function (value, el) {
                return parseInt(Quote.form.find('.js-reviewquoteid').val()) ? true : false;
            }
        },
        'pickUpDateTime': {
            message: 'We require at least 24hrs notice for metro pickups and 48hrs for rural pickups. Cut off time for next business day pickup up is 12:00 PM AEST.',
            callback: function (value, el) {
                var valid = false;
                var accCusPickuptime = Quote.form.find('.js-pickuptime').val();

                var data = {
                    PickUpDate: Quote.form.find('.js-pickupdate').val(),
                    PickUpTime: accCusPickuptime,
                    PickUpState: Quote.form.find('.js-pickupstate').val(),
                    PickUpDepot: Quote.pickUpDepot
                };

                $.ajax({
                    async: false,
                    type: "POST",
                    url: Quote.getUrlPath("/validatePickUpDatetime"),
                    data: data,
                    dataType: "json",
                    success: function (response) {
                        valid = response;
                    }
                });
                return valid;
            }
        },
        'suburbStatePostcode': {
            message: 'This field must be in the format of "Suburb, State, Postcode".',
            callback: function (value, el) {
                return !!value.trim().match(/^[^,]*\s*,\s*[a-zA-Z]*\s*,\s*\d{3,4}$/);
            }
        },
        'vehicleSpecifier': {
            message: 'This field is required.',
            callback: function (value, el) {
                return ((value.indexOf('-- Vehicle') === -1) && value.trim()) ? true : false;
            }
        },
        'valueIsTrue': {
            message: 'Only a value of "Yes" is valid for this field.',
            callback: function (value, el) {
                value = value.trim().toLowerCase();
                return ((value == 'yes') || (value == 'true')) ? true : false;
            }
        },
        'alphaNum': {
            message: 'Only alpha-numeric values are permitted in this field.',
            callback: function (value, el) {
                return value.match(/^[A-Za-z0-9]*$/) ? true : false;
            }
        },
        'emptySpaceOnly': {
            message: 'This field is empty or only contains spaces',
            callback: function (value, el) {
                return value.replace(/\s/g, '').length ? true : false
            }
        },
        'cevaCarValueMax': {
            message: 'The specified value of your vehicle exceeds the Website Maximum (150,000 AU$), please contact the Customer Care Centre on 13 CARS (13 2277) for a quote',
            callback: function (value, el) {
                if (value <= 150000) return true;
                else return false;
            }
        }
    };

    /**
     * Initialises the quote form. This is the entry point function.
     */
    Quote.init = function () {
        var $ = Quote.$;
        Quote.form = $('.js-quoteform');
        Quote.quotesTargetEl = $('.js-quotes');
        Quote.noQuoteMsgEl = $('.js-no-quote-msg');
        Quote.quoteTemplate = $('.js-quotetemplate').html();


        //Generate placeholder quotes for testing only
        //Quote.getQuotes();

        //Step nav config
        Quote.initSteps(1);
        Quote.setStepNavigationStatus(true);
        Quote.form.find('.js-jumptopayment').on('click', function (ev) {
            ev.preventDefault();
            Quote.jumpTo('#paymentarea');
        });

        //Validator config
        Quote.initValidation();
        $.each(Quote.validationMethods, function (index, methOb) {
            $.validator.addMethod(index, methOb.callback, methOb.message);
        });

        //Tooltip config
        Quote.form.tooltip({
            track: true
        });

        //Date picker config
        /*  function disableSpecificDaysAndWeekends(e) {
              var t = e.getMonth();
              var n = e.getDate();
              var r = e.getFullYear();
              for (var i = 0; i < disabledSpecificDays.length; i++) {
                  var s = $.inArray(r + "-" + (t + 1) + "-" + n, disabledSpecificDays);
                  if (s != -1 || new Date > e) {
                      var o = disabledSpecificDaysTitle[s];
                      return [false, "", o]
                  }
              }
              var u = $.datepicker.noWeekends(e);
              return !u[0] ? u : [true]
          }*/

        /*   var disabledSpecificDays = ["2018-3-30", "2018-4-2", "2018-4-25", "2018-12-25", "2018-12-26"];
           var disabledSpecificDaysTitle = ["Good Friday", "Easter Monday", "Anzac Day", "Christmas Day", "Boxing Day"];*/


        $(".js-pickupdate").datepicker({
            dateFormat: "dd-mm-yy",
            // minDate: 1, //with minDate we cant enable today selectable
            //  beforeShowDay: disableSpecificDaysAndWeekends,
            beforeShowDay: Quote.datePickerDateExclusionCallback,
            firstDay: 1
        });

        //Better Checkboxes
        $('.js-pickupsync,.js-deliverysync,.js-pickupsync-contactdetails, .js-deliverysync-pickup').checkboxradio({icon: false});

        //Multi-vehicle config
        Quote.vehicleTemplate = $('.js-vehicletemplate').html();
        Quote.vehiclesTargetEl = Quote.form.find('.js-vehicles');

        Quote.getExistingQuoteVehicles();

        if (!Quote.getVehicleCount()) {
            Quote.createVehicleEl();
        }

        //Depot selection address updates
        Quote.form.find('.js-deliverydepot').on('change', function (ev) {
            Quote.updateDepotLocation('delivery');
        });
        Quote.form.find('.js-pickupdepot').on('change', function (ev) {
            Quote.updateDepotLocation('pickup');
        });
        Quote.form.find('.js-pickuptype').on('change', function (ev) {
            var type = $(this).val() == '0' ? 'depot' : 'address';
            Quote.setLocationType('pickup', type, true);
        });
        Quote.form.find('.js-deliverytype').on('change', function (ev) {
            var type = $(this).val() == '0' ? 'depot' : 'address';
            Quote.setLocationType('delivery', type, true);
        });
        // Quote.form.find('.js-pickupquotedfieldslocked').on('click', function (ev) {
        //     Quote.form.find('.js-quoteselection').prop('checked', false);
        //     Quote.setLocationType('pickup', null, false);
        // });
        // Quote.form.find('.js-deliveryquotedfieldslocked').on('click', function (ev) {
        //     Quote.form.find('.js-quoteselection').prop('checked', false);
        //     Quote.setLocationType('delivery', null, false);
        // });
        Quote.setLocationType('pickup', 'depot');
        Quote.setLocationType('delivery', 'depot');
        Quote.form.find('.js-pickupsync,.js-deliverysync').on('change', function (ev) {
            Quote.syncDetails();
        });
        Quote.form.find('.js-deliverysync-pickup').on('change', function (ev) {
            Quote.syncPickupDetails();
        });
        Quote.form.find('.js-pickupsync-contactdetails').on('change', function (ev) {
            Quote.syncContactDetails();
        });
        //Inits suburb auto complete functionality
        //Assumes a particular format for the 'name' attribute in order to know which fields to update upon select.
        Quote.initSuburbAutocomplete(Quote.$('.js-addressautocomplete'), {
            select: function (ev, ui) {
                var selectorPrefix = '.js-' + Quote.$(ev.target).prop('name').replace('Suburb', '').toLowerCase();
                var selectedItem = ui.item;
                $(selectorPrefix + 'state').val(selectedItem.state);
                $(selectorPrefix + 'postcode').val(selectedItem.postcode);
            }
        });
        Quote.initSuburbAutocomplete(Quote.$('.js-suburbautocomplete'), {
            response: function (ev, ui) {
                Quote.$.each(ui.content, function (index, item) {
                    item.value = item.label;
                });
            },
            select: function (ev, ui) {
                let selectedItem = ui.item;
                if (selectedItem.type === 'RUR') {
                    ev.target.parentElement.querySelector('.js-suburb-itext').innerHTML = ('The location selected may need to be a ‘meet and greet’.');
                    $(ev.target.parentElement).addClass("tooltip-active");
                } else {
                    // ev.target.parentElement.querySelector('.step-notation').style.display = 'none';
                    ev.target.parentElement.querySelector('.js-suburb-itext').innerHTML = ('Please type first four letters of suburb and select suburb from list');
                }
            }
        });

        // search past quotes
        $('.js-searchQuoteBtn').on('click', function (e) {
            e.preventDefault();
            $('.js-trackingresults').addClass('loading');
            $('.js-spinner').show();
            var search_option = $('.js-searchoptions').val();
            if (search_option == "1") {
                var data = {
                    fromDate: $('.js-fromDate').val(),
                    toDate: $('.js-toDate').val()
                };

            } else {
                var data = {
                    quoteNo: $('.js-searchQuoteID').val()
                };
            }
            $.ajax({
                type: "POST",
                url: Quote.getUrlPath('/searchQuotes'),
                data: data,
                success: function (data) {
                    if (data) {
                        $('.js-quote-search-results').html(data);
                    }
                    $('.js-trackingresults').removeClass('loading');
                    $('.js-spinner').hide();
                }
            });
        });

        // New Quote search form
        if ($('.js-searchoptions').length) {
            $('.js-searchoptions').on('change', function () {
                if (this.value == '1') {
                    $('.js-searchQuoteID').val('');
                    $('.js-quoteIDSearch').hide();
                    $('.js-dateRangeSearch').show();
                } else {
                    $('.js-quoteIDSearch').show();
                    $('.js-fromDate').val("01/01/2018");
                    $('.js-toDate').val("01/01/2018");
                    $('.js-dateRangeSearch').hide();
                }
            });
        }

        /*// select account dropdown
        if ($('.js-getDetailsFrom').length) {
            var data = $('.js-getDetailsFrom').data("accountinfo");

            var preselectedAcc = $('.js-getDetailsFrom').data("preselected"); // for loading quote function

            if (preselectedAcc) { // for loading quote function
                $('.js-getDetailsFrom').val(preselectedAcc);
                $.ajax({
                    type: "POST",
                    url: Quote.getUrlPath('/setSelectedAccCusNo'),
                    data: {
                        AccCusNo: preselectedAcc,
                    },
                    success: function (data) {
                        console.log("Account changed");
                    }
                });
            } else {
                $('input[name="ContactEmail"]').val(data.Email);
                // var names = data.Name.split(" ");
                // $('input[name="ContactFirstName"]').val(names[0]);
                // $('input[name="ContactSurname"]').val(names[1]);
                // $('input[name="ContactPhone"]').val(data.Phone);
                // $('input[name="ContactMobile"]').val(data.Mobile);
            }

            $('.js-getDetailsFrom').on('change', function (ev) {
                var choice = $(this).val();
                // Set account customer number to make quotes and booking with
                // Call ajax to a function to update account customer number
                $.ajax({
                    type: "POST",
                    url: Quote.getUrlPath('/setSelectedAccCusNo'),
                    data: {
                        AccCusNo: choice,
                    },
                    success: function (data) {
                        console.log("Account changed");
                    }
                });
            });
        }

    */
        //Load quote functionality
        // Quote.form.find('.js-loadquote').on('click', Quote.retrieveExistingQuote($(this).data('quoteid')));
        //Delay notices
        var dialog = $(".js-delaynotice").dialog({
            autoOpen: false,
            height: 410,
            width: 550,
            modal: true
        });
        $('.js-delaynoticetrigger').on('click', function (ev) {
            ev.preventDefault();
            dialog.dialog('open');
        });

        // set the acccusno
        $.ajax({
            type: "POST",
            url: Quote.getUrlPath('/setSelectedAccCusNo'),
            data: {
                AccCusNo: $('.js-getDetailsFrom').val(),
            },
            success: function (data) {
                console.log("Account default");
            }
        });
    };

    Quote.addAnotherVehicleClick = function () {
        Quote.addVehiclesTargetEl = Quote.form.find('.js-addvehicle');
        Quote.addVehiclesTargetEl.on('click', function (ev) {
            ev.preventDefault();

            if (Quote.getVehicleCount() < 20) {
                var newVehicleEl = Quote.createVehicleEl();
                Quote.jumpTo(newVehicleEl);
            } else {
                Quote.alertMessage("You have reached the limit of 20 vehicles per quote.");
            }
        });
    }

    Quote.initPrint = function () {
        //Print
        $('.js-print').on('click', function (ev) {
            ev.preventDefault();
            window.print();
        });
    };

    /**
     * Initialises the step navigation bar.
     */
    Quote.initSteps = function (showStep) {
        var triggers = Quote.$('.js-steptrigger');
        triggers.each(function (index, el) {
            var el = Quote.$(el);
            el.on('click', function (ev) {
                ev.preventDefault();

                // show next buttons when previous buttons are clicked,
                // because some next buttons are disabled when there's errors
                if ($(this).hasClass('js-steptrigger-previous')) {
                    $('.js-steptrigger-next').show();
                }

                var stepNum = el.data('stepTarget');
                if (!Quote.$.isNumeric(stepNum)) {
                    Quote.$.error('The step target must be a number. "' + stepNum + '" was provided.');
                }
                Quote['showStep' + stepNum]();
            })
        });
        Quote.$('.js-steptarget').hide();
        if (showStep) {
            Quote.showStep(showStep);
        }
    };

    /**
     * Initialises the on-change functionality for the vehicles 'make' select field.
     *
     * @param {jQuery} The DOM element holding all of the fields for a single vehicle.
     */
    Quote.initMakeSelector = function (vehicleEl) {
        vehicleEl.find('.js-makecode').on('change', function (ev) {
            ev.preventDefault();
            Quote.getModel(vehicleEl);
        });
    };

    /**
     * Populates the 'make' select field with options.
     *
     * @param {jQuery} The DOM element holding all of the fields for a single vehicle.
     * @param {array} An array of object literals with each object having 'MakeCode' and 'Description' properties.
     * @param {string} The value of the option that should be currently selected.
     */
    Quote.populateMakeOptions = function (vehicleEl, data, selectedVal) {
        var makeEl = vehicleEl.find('.js-makecode');
        makeEl.empty();
        makeEl.append($('<option>', {html: "-- Vehicle Model --"}));
        $.each(data, function (index, make) {
            makeEl.append($('<option>', {value: make.MakeCode, html: make.Description}));
        });
        if (selectedVal) {
            makeEl.val(selectedVal);
            Quote.updateMakeDescription(vehicleEl);
        }
    };

    /**
     * Initialises the on-change functionality for the vehicles 'model' select field.
     *
     * @param {jQuery} The DOM element holding all of the fields for a single vehicle.
     */
    Quote.initModelSelector = function (vehicleEl) {
        vehicleEl.find('.js-modelcode').on('change', function (ev) {
            ev.preventDefault();
            Quote.getTypes(vehicleEl);
        });
    };

    /**
     * Populates the 'model' select field with options.
     *
     * @param {jQuery} The DOM element holding all of the fields for a single vehicle.
     * @param {array} An array of object literals with each object having 'ModelCode' and 'Description' properties.
     * @param {string} The value of the option that should be currently selected.
     */
    Quote.populateModelOptions = function (vehicleEl, data, selectedVal) {
        var modelEl = vehicleEl.find('.js-modelcode');
        modelEl.empty();
        modelEl.append($('<option>', {html: "-- Vehicle Model --"}));
        $.each(data, function (index, model) {
            modelEl.append($('<option>', {value: model.ModelCode, html: model.Description}));
        });
        if (selectedVal) {
            modelEl.val(selectedVal);
            Quote.updateModelDescription(vehicleEl);
        }
    }

    /**
     * Initialises the on-change functionality for the vehicles 'type' select field.
     *
     * @param {jQuery} The DOM element holding all of the fields for a single vehicle.
     */
    Quote.initTypeSelector = function (vehicleEl) {
        vehicleEl.find('.js-typecode').on('change', function (ev) {
            ev.preventDefault();
            Quote.updateTypeDescription(vehicleEl);
        });
    };

    /**
     * Populates the 'type' select field with options.
     *
     * @param {jQuery} The DOM element holding all of the fields for a single vehicle.
     * @param {array} An array of object literals with each object having 'TypeCode' and 'Description' properties.
     * @param {string} The value of the option that should be currently selected.
     */
    Quote.populateTypeOptions = function (vehicleEl, data, selectedVal) {
        var $ = Quote.$;
        var typeEl = vehicleEl.find('.js-typecode');
        typeEl.empty();
        if (data.length) {
            $.each(data, function (index, type) {
                typeEl.append($('<option>', {value: type.TypeCode, html: type.Description}));
            });
        } else {
            typeEl.append($('<option>', {html: '-- Vehicle Type --'}));
        }
        if (selectedVal) {
            typeEl.val(selectedVal);
            Quote.updateTypeDescription(vehicleEl);
        }
    }

    /**
     * Initialises the functionality of the select and the related +- than 60k input field
     *
     * @param {jQuery} The DOM element holding all of the fields for a single vehicle.
     */
    Quote.initVehicleValue = function (vehicleEl) {
        //Vehicle Value config
        var lowValue = vehicleEl.find('.js-lowvalue');
        var exactValue = vehicleEl.find('.js-exactvalue');
        var exactValueWrap = vehicleEl.find('.js-exactvaluewrap');
        if (!exactValue.val()) {
            exactValue.val('60000');
            exactValue.attr('readonly', 'readonly');
            exactValueWrap.hide();
        }
        lowValue.on('change', function (ev) {
            var choice = ($(this).val() === 'true');
            if (choice) {
                exactValue.val('60000');
                exactValue.attr('readonly', 'readonly');
                exactValueWrap.hide();
            } else {
                exactValue.removeAttr('readonly');
                exactValueWrap.show();
            }
        });
    };


    /**
     * Initialises the functionality of the 'remove vehicle' button for a single vehicle.
     *
     * @param {jQuery} The DOM element holding all of the fields for a single vehicle.
     */
    Quote.initVehicleRemoveBtn = function (vehicleEl) {
        vehicleEl.find('.js-removevehicle').on('click', function (ev) {
            ev.preventDefault();
            if (Quote.getVehicleCount() > 1) {
                vehicleEl.remove();
            } else {
                Quote.alertMessage('You can\'t remove this vehicle. You must have at least one.');
            }
        });
    };

    Quote.initValidation = function () {
        //See https://jqueryvalidation.org/rules for why
        var rules = {};
        $.each(Quote.validationRules, function (groupName, groupRules) {
            $.extend(rules, groupRules);
        });
        var validator = Quote.form.validate({
            ignore: ".js-skipvalidation",
            rules: rules,
            messages: Quote.validationMessages,
            errorPlacement: Quote.validationPlacements
        });
    };

    /**
     * @return {string} Returns the path part of the current url excluding any trailing slashes.
     */
    Quote.getUrlPath = function (append) {
        var append = append ? append : '';
        var home = (window.location.pathname == '' || window.location.pathname == '/') ? '/home' : '';
        return window.location.pathname.replace(/\/$/, '') + home + append;
    };


    /**
     * Alert box
     *
     * @todo Use something other than the default JS alert() function.
     */
    Quote.alertMessage = function (msg) {
        alert(msg);
    };


    /**
     * Changes the step that is shown to the user. This method should always be used as it sets the 'current'
     * step. There is no validation as to whether the action can be performed or not.
     *
     * @param num The number of the step to jump to (starting at 1).
     * @param jump Scroll the view to the top of the step.
     */
    Quote.showStep = function (num, jump) {
        var triggers = Quote.$('.js-steptrigger');
        triggers.removeClass('active');
        triggers.removeClass('completed');
        triggers.each(function () {
            var curr = $(this).data('step-target');
            if (curr < num) $(this).addClass('completed');
        });
        triggers.filter('[data-step-target="' + num + '"]').addClass('active');
        var targets = Quote.$('.js-steptarget');
        targets.hide();
        var showEl = targets.filter('[data-step-target="' + num + '"]').show();
        if (showEl.length) {
            Quote.currentStep = num;
        }
        if (jump) {
            Quote.jumpTo('#quote_steps');
        }
        return showEl.length ? true : false;
    };

    /**
     * Validates and changes to Step 1.
     */
    Quote.showStep1 = function () {
        if (!Quote.validateStepChange(1)) {
            return;
        }
        Quote.showStep(1, true);
    };

    /**
     * Validates and changes to Step 2.
     */
    Quote.showStep2 = function () {
        if (!Quote.validateStepChange(2)) {
            return;
        }
        Quote.getQuotes(Quote.autoSelectQuote);
        Quote.showStep(2, true);
    };

    /**
     * Validates and changes to Step 3.
     */
    Quote.showStep3 = function () {
        if (!Quote.validateStepChange(3)) {
            return;
        }
        Quote.submitQuote();

        Quote.showStep(3, true);
        $('.js-steptrigger-next').find('.text').html('Book now');
    };

    /**
     * Validates and changes to Step 4.
     */
    Quote.showStep4 = function () {
        if (!Quote.validateStepChange(4)) {
            return;
        }

        $('.js-continue-button').on('click', function (e) {
            e.preventDefault();
            var section = $(this).data('section');
            var valid = Quote.validate('step' + section);
            if (valid) {
                $('.js-section-' + section).removeClass('active').addClass('completed');
                $('.js-section-' + section).next('.step-section').addClass('active');

                const activeSection = document.querySelector('.step-section.active');
                if (activeSection) {
                    window.scrollTo({
                        top: activeSection.offsetTop,
                        behavior: 'smooth'
                    });
                }

                var isTermsOfUseAgreed = $('.js-termsofuse').prop("checked");
                // Hide and Show Next Step depending on the logic
                if (section == '4c' && isTermsOfUseAgreed) {
                    $('.step_4 .js-steptrigger-next').show();
                } else {
                    $('.step_4 .js-steptrigger-next').hide();
                }
            } else {
                Quote.jumpTo($('.step-section.active'));
            }
        });
        Quote.form.find('.js-step-section-title').on('click', function (e) {
            e.preventDefault();
            var oldActiveSectionEl = Quote.form.find('.step-section.active');
            var oldActiveSectionValue = oldActiveSectionEl.data('section');
            var newActiveSectionEl = $(this).parents('.step-section');
            var newActiveSectionValue = newActiveSectionEl.data('section');
            var isOldSectionValid = Quote.validate('step' + oldActiveSectionValue);
            if (isOldSectionValid) {
                var isNewActiveSectionCompleted = newActiveSectionEl.hasClass('completed');
                if (isNewActiveSectionCompleted) {
                    oldActiveSectionEl.removeClass('active').addClass('completed');
                    newActiveSectionEl.removeClass('completed').addClass('active');
                    var isTermsOfUseAgreed = $('.js-termsofuse').prop("checked");

                    // Hide and Show Next Step depending on the logic
                    if (newActiveSectionValue == '4d' && isTermsOfUseAgreed) {
                        $('.step_4 .js-steptrigger-next').show();
                    } else {
                        $('.step_4 .js-steptrigger-next').hide();
                    }
                }
            }
        });

        Quote.getExcludedPickUpDates(true);
        $(".js-pickupdate").datepicker({
            dateFormat: "yy-mm-dd",
            // minDate: 1,
            beforeShowDay: Quote.datePickerDateExclusionCallback,
            firstDay: 1
        });
        $('.js-termsofuse').change(function () {
            if ($(this).prop("checked")) {
                $('.step_4 .js-steptrigger-next').show();
            } else {
                $('.step_4 .js-steptrigger-next').hide();
            }
        });
        Quote.showStep(4, true);
        $('.js-steptrigger-next').find('.text').html('Confirm Booking');
    };

    /**
     * Validates and changes to Step 5.
     */
    Quote.showStep5 = function () {
        if (!Quote.validateStepChange(5)) {
            return;
        }
        Quote.submitBooking(function () {
            Quote.showStep(5, true);
            Quote.setStepNavigationStatus(false);
        });
    };

    Quote.setStepNavigationStatus = function (status) {
        var $ = Quote.$;
        if ($.type(status) != 'boolean') {
            $.error('You must provide a boolean value.');
        }
        Quote.stepNavigationStatus = status;
        var triggers = Quote.form.find('.js-steptrigger');
        if (status) {
            triggers.removeClass('inactive');
        } else {
            triggers.addClass('inactive');
        }
    };

    Quote.validateStepChange = function (num) {
        // return true;
        if (!Quote.stepNavigationStatus) {
            Quote.alertMessage('You are not currently permitted to change steps.');
            return false;
        }
        var $ = Quote.$;
        var steps = [1, 2, 3, 4, 5, 6];
        //Can only move forward one step at a time.
        var maxStep = Quote.currentStep + 1;
        if (num > maxStep) {
            Quote.alertMessage('You cannot skip a step. Please go to step ' + maxStep + ' next.');
            return false;
        }
        var valid = true;
        $.each(steps, function (index, stepNum) {
            if ((stepNum < num) && !Quote.validate('step' + stepNum)) {
                Quote.alertMessage("Please fix the errors shown in red on step " + stepNum + " before continuing.");
                Quote.showStep(stepNum, true);
                valid = false;
                return false;
            }
        });
        return valid;
    };
    Quote.jumpTo = function (selectorOrObject, speed) {
        var speed = speed ? speed : 200;
        Quote.$('html, body').stop().animate({
            'scrollTop': Quote.$(selectorOrObject).offset().top - 150
        }, speed, 'swing');
    };

    /**
     * Performs validation on fields within one or more steps or the whole form.
     *
     * @param {string|array} group The name of the step/validation-rule-group to validate. Can also be an array of step
     * names. Do not provide an argument for all steps/whole form.
     * @return {boolean}
     */
    Quote.validate = function (group) {
        var $ = Quote.$;
        var rules = {};
        if (!group) {
            var group = ['step1', 'step2', 'step3', 'step4', 'step5'];
        }
        if ($.type(group) == 'string') {
            group = [group];
        }
        if ($.type(group) != 'array') {
            $.error('The argument provided to Quote.validate() must be an array.');
        }

        //Add/remove class to ignore fields for validation.
        $.each(Quote.validationRules, function (groupName, rules) {
            $.each(rules, function (fieldName) {
                var field = Quote.form.find('[name="' + fieldName + '"]');
                if ($.inArray(groupName, group) !== -1) {
                    field.removeClass('js-skipvalidation');
                } else {
                    field.addClass('js-skipvalidation');
                }
            });
        });

        //Add/remove class to ignore vehicle fields for validation.
        var vehicleInputs = Quote.form.find('.js-vehicle input,.js-vehicle select');
        if ($.inArray('step1', group) !== -1) {
            vehicleInputs.removeClass('js-skipvalidation');
        } else {
            vehicleInputs.addClass('js-skipvalidation');
        }

        //Ensure all inputs are enabled when performing validation then reset to previous states.
        var disabled = Quote.form.find(':disabled');
        disabled.attr('disabled', false);
        var ret = Quote.form.valid();
        disabled.attr('disabled', true);
        return ret;
    };

    /**
     * Briefly enable all disabled elements, run callback provided and then return elements to their previous state.
     * Originally implemented to ensure that validation would run on all form elements.
     *
     * @param {function} callback The function to run while all elements are enabled.
     * @return {mixed}
     */
    Quote.suspendDisabledInputs = function (callback) {
        var disabled = Quote.form.find(':disabled').prop('disabled', false);
        var resp = callback();
        disabled.prop('disabled', true);
        return resp;
    };

    Quote.populateQuoteEl = function (quoteData) {
        var template = $('.js-quotetemplate');
        template.find('.js-quote').each(function (index) {
            if (
                quoteData.deliveryType == parseInt($(this).data('deliverytype')) &&
                quoteData.pickUpType == parseInt($(this).data('pickuptype'))
            ) {
                $(this).find('[data-servicetype=' + quoteData.service + '] .js-transit-days').show();
                $(this).find('.blackened').each(function (index, blackend) {
                    $(this).show();
                });

                $(this).find('[data-servicetype=' + quoteData.service + '] .js-quote-cost').html(quoteData.totalNice);
                $(this).find('[data-servicetype=' + quoteData.service + '] .js-transit-days').html('Transit Days: ' + quoteData.transitDays);
                $(this).find('.js-quotepickup').html('<i>' + quoteData.pickUp + '</i>').addClass('locationtype-' + quoteData.pickUpType);
                $(this).find('.js-quotedelivery').html('<i>' + quoteData.delivery + '</i>').addClass('locationtype-' + quoteData.deliveryType);

                // var quoteInput = $(this).find('.js-quoteselection');
                // var quoteInputLabel = $(this).find('.js-quoteselectionlabel');

                var quoteInput = $(this).find('[data-servicetype=' + quoteData.service + ']').find('.js-quoteselection');
                var quoteInputLabel = $(this).find('[data-servicetype=' + quoteData.service + ']').find('.js-quoteselectionlabel');
                quoteInput.val(quoteData.uniqueId);
                quoteInput.attr('id', 'Quote-' + quoteData.uniqueId);

                $(this).find('[data-servicetype=' + quoteData.service + ']').addClass('visible');

                quoteInput.on('change', function (ev) {
                    Quote.updateQuoteSelection();
                });

                quoteInputLabel.attr('for', 'Quote-' + quoteData.uniqueId);

                if (quoteData.selected) {
                    quoteInput.prop("checked", true).trigger("change");
                }
            }
        });
    }

    Quote.populateNullQuote = function () {
        var template = $('.js-quotetemplate');
        template.find('.cost-col').each(function (index, quoteCell) {
            if (!$(quoteCell).hasClass('visible')) {
                $(quoteCell).find('.js-quote-cost').html('N/A');
                $(quoteCell).find('.js-transit-days').hide();
                $(quoteCell).find('.blackened').hide();
                $(quoteCell).addClass('visible');
            }
        });
    }

    Quote.updateQuoteSelection = function () {
        var $ = Quote.$;
        var selectedEl = Quote.form.find('.js-quoteselection:checked');

        //Add class to selected quote
        Quote.form.find('.js-quote').removeClass('active');
        selectedEl.parents('.js-quote').addClass('active');

        var quoteId = selectedEl.val();
        var quoteData = Quote.getQuoteById(quoteId);
        if (quoteData) {
            var pickUpType = quoteData.pickUpType == '0' ? 'depot' : 'address';
            var deliveryType = quoteData.deliveryType == '0' ? 'depot' : 'address';
            Quote.setLocationType('pickup', pickUpType, true, true);
            Quote.setLocationType('delivery', deliveryType, true, true);
            if (pickUpType == 'depot') {
                Quote.form.find('.js-pickuptype').val('0');
                Quote.form.find('.js-pickupdepot').val(quoteData.pickUpDepotCode);
                Quote.updateDepotLocation('pickup', quoteData.pickUpDepotCode);
            }
            if (deliveryType == 'depot') {
                Quote.form.find('.js-deliverytype').val('0');
                Quote.form.find('.js-deliverydepot').val(quoteData.deliveryDepotCode);
                Quote.updateDepotLocation('delivery', quoteData.deliveryDepotCode);
            }
            var pickUpLocation = Quote.getPickUpLocation();
            if ((pickUpType == 'address') && pickUpLocation) {
                Quote.form.find('.js-pickupsuburb').val(pickUpLocation.suburb);
                Quote.form.find('.js-pickupstate').val(pickUpLocation.state);
                Quote.form.find('.js-pickuppostcode').val(pickUpLocation.postcode);
            }
            var deliveryLocation = Quote.getDeliveryLocation();
            if ((deliveryType == 'address') && deliveryLocation) {
                Quote.form.find('.js-deliverysuburb').val(deliveryLocation.suburb);
                Quote.form.find('.js-deliverystate').val(deliveryLocation.state);
                Quote.form.find('.js-deliverypostcode').val(deliveryLocation.postcode);
            }

        }
    };

    /**
     * Retrieves multiple quotes remotely based on inputs from step 1 (locations & vehicles). Updates the interface elements
     * accordingly.
     */
    Quote.getQuotes = function (callback) {
        var $ = Quote.$;
        var callback = typeof callback == 'function' ? callback : new Function;
        var quoteTableContainer = $('.js-quotes-table-container');
        Quote.noQuoteMsgEl.empty();


        var quotesWrapper = $('.js-quotesloadingwrapper').addClass('loading');
        $.ajax({
            type: "POST",
            url: Quote.getUrlPath('/getQuotes'),
            data: {
                PickUpLocation: Quote.form.find('.js-pickuplocation').val(),
                DeliveryLocation: Quote.form.find('.js-deliverylocation').val(),
                Vehicles: Quote.getVehiclesFormData()
            },
            dataType: 'json',
            success: function (data) {
                quotesWrapper.removeClass('loading');
                Quote.quotes = data;
                var isEmptyRated = false;

                if (data && data.length && (isEmptyRated != true)) {
                    $.each(data, function (index, quoteData) {
                        Quote.populateQuoteEl(quoteData);
                    });
                    Quote.populateNullQuote();
                    quoteTableContainer.show();


                    // let vehicles = Quote.getVehiclesFormData();
                    // quoteTableContainer.find('.js-pono').val(vehicles[0]['VehicleId']);

                } else {
                    var noQuoteMsg = "<p>Sorry. We can't provide quotes for the types of vehicles you want to move. " +
                        "Please call 13 2277 and one of our customer care representative will be able to help.</p>";
                    if (isEmptyRated == true) {
                        var noQuoteMsg = "<p>Weâ€™re sorry! There was an problem processing your request. Please call 13 CARS (13 2277) or <a href='mailto:CCC@Cevalogistics.com'>email CCC@Cevalogistics.com</a> and one of our Customer Care Representatives will be able to assist you.</p>";
                    }
                    Quote.noQuoteMsgEl.html(noQuoteMsg);
                    // hide next button, show it back when a previous button is clicked (in Quote.initSteps)
                    $('.steptriggers').find('.next').hide();
                }
                callback();
            }
        });
    };

    /**
     * Gets quote data from cached results (previously obtained from Quote.getQuotes()) based on provided quote ID.
     *
     * @param {string} The quote id to find.
     * @param {Object} The matching quote data.
     */
    Quote.getQuoteById = function (id) {
        var $ = Quote.$;
        if (Quote.quotes && Quote.quotes.length) {
            var matching = $.grep(Quote.quotes, function (quoteData) {
                return quoteData.uniqueId == id;
            });
            if (matching.length) {
                return matching[0];
            }
        }
        return false;
    };

    Quote.autoSelectQuote = function () {
        var comb = Quote.form.find('.js-autoselectquote').val();
        if (comb) {
            var types = comb.split('-');
            var serviceType = Quote.form.find('.js-autoselectquote').data('servicetype');
            if (types.length == 2) {
                var quoteEls = Quote.form.find('.js-quotetemplate .js-quote');
                quoteEls.each(function (index, quoteEl) {
                    var quoteEl = Quote.$(quoteEl);

                    if ($(quoteEl).data('pickuptype') == types[0] && $(quoteEl).data('deliverytype') == types[1]) {
                        // do something here to pre-select the quote
                        $(quoteEl).find("div[data-servicetype=" + serviceType + "]").find('.js-quoteselection').trigger('click');
                        // debugger;
                    }
                });

            }
        }
    };

    Quote.createVehicleEl = function () {
        var vehicleEl = $(Quote.vehicleTemplate);
        Quote.initMakeSelector(vehicleEl);
        Quote.initModelSelector(vehicleEl);
        Quote.initTypeSelector(vehicleEl);
        Quote.initVehicleRemoveBtn(vehicleEl);
        Quote.initVehicleValue(vehicleEl);
        Quote.vehiclesTargetEl.append(vehicleEl);
        Quote.initVehicleValidation(vehicleEl);
        Quote.addAnotherVehicleClick();
        return vehicleEl;
    };

    Quote.getExcludedPickUpDates = function (refresh) {
        if (refresh || (Quote.excludedPickUpDates === null)) {
            $.ajax({
                async: false,
                url: Quote.getUrlPath("/excludedPickUpDates"),
                data: {
                    PickUpDate: Quote.form.find('.js-pickupdate').val(),
                    PickUpTime: Quote.form.find('.js-pickuptime').val(),
                    PickUpState: Quote.form.find('.js-pickupstate').val(),
                    PickUpDepot: Quote.pickUpDepot
                },
                dataType: "json",
                success: function (response) {
                    if (response.length) {
                        Quote.excludedPickUpDates = response;
                    } else {
                        throw "Error in retrieving excluded pickup dates";
                    }
                }
            });
        }
        return Quote.excludedPickUpDates;
    };

    Quote.getExistingQuoteVehicles = function (quoteId) {
        // debugger;
        var $ = Quote.$;
        if (!quoteId) {
            var searchParams = new URLSearchParams(window.location.search);
            if (searchParams.has('qid')) {
                var quoteId = searchParams.get('qid');
            } else {
                var quoteId = '';
            }
        }
        if (!quoteId.length) {
            return false;
        }
        $.ajax({
            async: false,
            type: "GET",
            url: Quote.getUrlPath('/getExistingQuoteVehicles'),
            data: {qid: quoteId},
            dataType: 'json',
            success: function (data) {
                if (data && data.length) {
                    $.each(data, function (index, vehicleData) {
                        var vehicleEl = Quote.createVehicleEl();
                        if (!$.isEmptyObject(vehicleData.makes)) {
                            Quote.populateMakeOptions(vehicleEl, vehicleData.makes, vehicleData.vehicle.VehicleMake);
                            if (!$.isEmptyObject(vehicleData.models)) {
                                Quote.populateModelOptions(vehicleEl, vehicleData.models, vehicleData.vehicle.VehicleModel);
                                if (!$.isEmptyObject(vehicleData.types)) {
                                    Quote.populateTypeOptions(vehicleEl, vehicleData.types, vehicleData.vehicle.VehicleType);
                                }
                            }
                        }
                        if (vehicleData.vehicle.VehicleId) {
                            vehicleEl.find('.js-vehicleid').val(vehicleData.vehicle.VehicleId);
                        }
                        if (vehicleData.vehicle.VehicleValue) {
                            vehicleEl.find('.js-vehiclevalue').val(vehicleData.vehicle.VehicleValue);
                        }
                    });
                }
            }
        });
    }

    /**
     * Gets and formats the form field data for vehicles.
     *
     * @return {Object[]} Returns an array of objects where the properties are the form field names and the values are the
     * values of those fields.
     */
    Quote.getVehiclesFormData = function () {
        var $ = Quote.$;
        var vehicles = [];
        Quote.form.find('.js-vehicle').each(function (index, vehicleEl) {
            var vehicleEl = $(vehicleEl);
            var vehicleData = {};
            vehicleEl.find('input,select').each(function (index, inputEl) {
                var inputEl = $(inputEl);
                var matches = inputEl.prop('name').match(/^.*\[(\w*)\]$/);
                if (matches.length == 2) {
                    vehicleData[matches[1]] = inputEl.val();
                }
            });
            vehicles.push(vehicleData);
        });
        return vehicles;
    };

    /**
     * Updates the MakeDescription field with the text of the MakeCode field.
     */
    Quote.updateMakeDescription = function (vehicleEl) {
        vehicleEl.find('.js-makedescription').val(vehicleEl.find('.js-makecode option:selected').text());
    };

    /**
     * Gets remote vehicle model data and populates ModelCode select field.
     */
    Quote.getModel = function (vehicleEl) {
        var $ = Quote.$;
        Quote.updateMakeDescription(vehicleEl)
        var modelSelector = vehicleEl.find('.js-modelcode');
        var modelOptSelected = modelSelector.find('option:selected');
        modelOptSelected.text("LOADING...");
        $.ajax({
            type: "POST",
            url: Quote.getUrlPath('/getModels'),
            data: {
                MakeCode: vehicleEl.find(".js-makecode").val(),
            },
            dataType: 'json',
            success: function (data) {
                Quote.populateModelOptions(vehicleEl, data);
                modelSelector.focus();
                vehicleEl.find('.js-typecode').empty().append('<option value="">-- Vehicle Type --</option>');
            }
        });
    };

    /**
     * Updates the ModelDescription field with the text of the ModelCode field.
     */
    Quote.updateModelDescription = function (vehicleEl) {
        vehicleEl.find('.js-modeldescription').val(vehicleEl.find('.js-modelcode option:selected').text());
    };

    /**
     * Gets remote vehicle type data and populates TypeCode select field.
     */
    Quote.getTypes = function (vehicleEl) {
        var $ = Quote.$;
        Quote.updateModelDescription(vehicleEl);
        var typeSelector = vehicleEl.find('.js-typecode');
        var typeOptSelected = typeSelector.find('option:selected');
        typeOptSelected.text("LOADING...");
        $.ajax({
            type: "POST",
            url: Quote.getUrlPath("/getTypes"),
            data: {
                ModelCode: vehicleEl.find(".js-modelcode").val()
            },
            dataType: 'json',
            success: function (data) {
                Quote.populateTypeOptions(vehicleEl, data);
                typeSelector.focus();
                Quote.updateTypeDescription(vehicleEl);
            }
        });
    };

    /**
     * Updates the TypeDescription field with the text of the TypeCode field.
     */
    Quote.updateTypeDescription = function (vehicleEl) {
        vehicleEl.find('.js-typedescription').val(vehicleEl.find(".js-typecode option:selected").text());
    };

    Quote.getVehicleEls = function () {
        return Quote.$('.js-vehicles').find('.js-vehicle');
    };

    Quote.getVehicleCount = function () {
        return Quote.getVehicleEls().length;
    };

    /**
     * @return {object} Returns an object literal with keys 'suburb', 'state' and 'postcode' representing current value of
     * the pick up or delivery location field.
     */
    Quote.getLocationAsObject = function (direction) {
        var selector = '.js-' + direction + 'location';
        var val = Quote.$(selector).val();
        var parts = val.split(',');
        var parts = $.map(parts, function (part) {
            return part.trim();
        });
        if (parts.length != 3) {
            $.error("Pick Up and Delivery location does not have all three required parts.");
            return false;
        }
        return {
            'suburb': parts[0],
            'state': parts[1],
            'postcode': parts[2]
        };
    };

    Quote.getPickUpLocation = function () {
        return Quote.getLocationAsObject('pickup');
    };

    Quote.getDeliveryLocation = function () {
        return Quote.getLocationAsObject('delivery');
    };

    /**
     * Modifies (hides, shows, disables, etc) the direction address ('pickup' or 'delivery') fields according to the
     * type of address ('depot' or 'address').
     *
     * @param {string} direction Can be 'delivery' or 'pickup'. Used to build the appropriate selector for a set of fields.
     * @param {string} type Can be 'depot' or 'address'. Used to modify the fields accordingly.
     * @param {Boolean} reset Determines if the values of the fields should be cleared.
     */
    Quote.setLocationType = function (direction, type, reset, fromQuote) {
        var $ = Quote.$;
        var selectorPrefix = '.js-' + direction;
        var fromQuote = !!fromQuote;

        //Get reusable elements
        var address1Input = Quote.form.find(selectorPrefix + 'address1');
        var address2Input = Quote.form.find(selectorPrefix + 'address2');
        var depotInput = Quote.form.find(selectorPrefix + 'depot');
        var typeInput = Quote.form.find(selectorPrefix + 'type');
        var suburbInput = Quote.form.find(selectorPrefix + 'suburb');
        var stateInput = Quote.form.find(selectorPrefix + 'state');
        var postcodeInput = Quote.form.find(selectorPrefix + 'postcode');
        var addressWrapperEl = Quote.form.find(selectorPrefix + 'addresswrapper');
        var depotWrapperEl = Quote.form.find(selectorPrefix + 'depotwrapper');
        var saveDefaultAddress = Quote.form.find(selectorPrefix + '-save-default-address');

        //Get currently selected type if not provided to method.
        if (!type) {
            var type = typeInput.val() == '0' ? 'depot' : 'address';
        }

        //Reset field values
        if (reset) {
            address1Input.val('');
            address2Input.val('');
            depotInput.val('');
            suburbInput.val('');
            stateInput.val('');
            postcodeInput.val('');
        }


        //Lock/unlock, hide/show according to type
        if (type == 'depot') {
            typeInput.val('0');
            suburbInput.attr('disabled', true);
            addressWrapperEl.hide();
            depotWrapperEl.show();
            saveDefaultAddress.hide();
            loadDefaultAddress.hide();
            // hide Get Address buttons
            $(selectorPrefix + '-get-from-address-container').hide();
            Quote.initStep1LoadAddress();
        } else if (type == 'address') {
            typeInput.val('1');
            suburbInput.attr('disabled', false);
            addressWrapperEl.show();
            depotWrapperEl.hide();
            saveDefaultAddress.show();
            loadDefaultAddress.show();
            // show Get Address buttons
            $(selectorPrefix + '-get-from-address-container').show();
            Quote.initStep1LoadAddress();
        }

        //Common to both
        stateInput.attr('disabled', true);
        postcodeInput.attr('disabled', true);

        //Lock fields that shouldn't be editable after selecting a quote.
        Quote.form.find(selectorPrefix + 'quotedfieldslocked').toggle(fromQuote);
        typeInput.attr('disabled', fromQuote);
        depotInput.attr('disabled', fromQuote);
        suburbInput.attr('disabled', fromQuote);
        if (!fromQuote && type == 'address') {
            suburbInput.attr('disabled', false);
        }
        if (!fromQuote && type == 'depot') {
            suburbInput.attr('disabled', true);
        }
    }

    /**
     * Updates the corresponding address, state, and postcode based on the selected depot for delivery or pickup type.
     *
     * @param {string} direction Can be 'delivery' or 'pickup'. Used to build the appropraite selector for a set of fields.
     * @param {string} depotCode If provided, the fields are updated with the corresponding depot details. If not provided,
     * the depot is obtained from the current depot selection in the interface.
     */
    Quote.updateDepotLocation = function (direction, depotCode) {
        var selectorPrefix = '.js-' + direction;
        if (!depotCode) {
            var depotCode = $(selectorPrefix + 'depot').val();
        }
        if (depotCode) {
            var depot = Quote.getDepotDetails(depotCode);
            if (depot) {
                $(selectorPrefix + 'suburb').val(depot.DepotAddress.Suburb.SuburbName);
                $(selectorPrefix + 'state').val(depot.DepotAddress.Suburb.State);
                $(selectorPrefix + 'postcode').val(depot.DepotAddress.Suburb.PostCode);
            }
        }
    };

    /**
     * Gets a depots full details by the depot code provided. Remote call is made synchronously.
     *
     * @param {string} code The requested depot's unique code.
     * @return {Object} An object with properties describing the depot.
     */
    Quote.getDepotDetails = function (code) {
        var depot = {};
        $.ajax({
            async: false,
            type: "POST",
            url: Quote.getUrlPath("/getDepotDetails"),
            data: {DepotCode: code},
            dataType: 'json',
            success: function (data) {
                depot = data;
            }
        });
        return depot;
    };

    Quote.submitQuote = function () {
        var $ = Quote.$;
        var paymentArea = Quote.form.find('.js-paymentarea').addClass('locked');
        var quoteResultArea = Quote.form.find('.js-quoteresultsarea').addClass('loading');

        var data = Quote.suspendDisabledInputs(function () {
            let vehicleID = Quote.form.serializeArray().find(item => item.name == 'Vehicles[0][VehicleId]');
            let quoteTableContainer = $('.js-quotes-table-container');
            Quote.form.serializeArray().forEach(function (item) {
                if (item.name == 'PONo' && !item.value) {
                    quoteTableContainer.find('.js-pono').val(vehicleID.value);
                }
                if (item.name == 'PickUpDepot') {
                    Quote.pickUpDepot = item.value
                }
            });
            return Quote.form.serialize();
        });

        $.ajax({
            type: "POST",
            url: Quote.getUrlPath("/submitQuote"),
            data: data,
            success: function (response, status, xhr) {
                quoteResultArea.removeClass('loading');
                $('.js-quoteresults').html(response).removeClass('loading');
                Quote.jumpTo('#quoteresultarea');
                $('.js-steptrigger-next').find('.text').html('Confirm Booking');
                if (response.indexOf("QuoteError:") === -1) {
                    if (paymentArea.length) {
                        paymentArea.removeClass('locked');
                    }
                }
            }
        });
    };

    Quote.submitBooking = function (onSuccess, onFailure) {
        var $ = Quote.$;
        var onSuccess = typeof onSuccess == 'function' ? onSuccess : new Function;
        var onFailure = typeof onFailure == 'function' ? onFailure : new Function;

        var data = Quote.suspendDisabledInputs(function () {
            return Quote.form.serialize();
        });

        if (!Quote.submitBookingDone) {
            var isContactInfoValid = Quote.validate('step4a');
            var isPickUpSectionValid = Quote.validate('step4b');
            var isDeliverSectionValid = Quote.validate('step4c');

            if (isContactInfoValid && isPickUpSectionValid && isDeliverSectionValid) {
                Quote.submitBookingDone = true;
                $.ajax({
                    type: "POST",
                    url: Quote.getUrlPath('/submitBooking'),
                    data: data,
                    success: function (response) {
                        // if (paymentArea.length) {
                        // 	paymentArea.removeClass('loading');
                        // }

                        if (response.indexOf('QuoteError:') === -1) {
                            Quote.form.find('.js-bookingresult').html(response);
                            Quote.initPrint();
                            onSuccess();
                        } else {
                            Quote.form.find('.js-bookingresult').html(response);
                            var errorEl = Quote.form.find('.js-paymenterror');
                            errorEl.html(response);
                            Quote.jumpTo(errorEl);
                            onFailure();
                        }
                    }
                });

            } else {
                Quote.alertMessage('You are missing some info, please check Contact, Pick Up and Delivery Details');
                return false;
            }
        } else {
            console.log('already submitted')
        }

        // var paymentArea = Quote.form.find('.js-paymentarea');
        // if (paymentArea.length) {
        // 	paymentArea.addClass('loading');
        // 	Quote.form.find('.js-paymenterror').html('');
        // }


        //Clear CC number field to prevent accidental re-submission.
        // if (paymentArea.length) {
        // 	Quote.form.find('.js-ccnumber').val('');
        // }


    };


    Quote.customValidate4b = function async () {
        // var pcName = Quote.form.find('[name="PickUpContactFirstName"]').val();
        // var pcSName = Quote.form.find('[name="PickUpContactSurname"]').val();
        // var pcMobile = Quote.form.find('[name="PickUpContactMobile"]').val();

        var result = true;

        result = Quote.form.serializeArray().find((item) => {
            if ((item.name === 'PickUpContactFirstName' && Quote.isEmptyNullOrSpace(item.value)) ||
                (item.name === 'PickUpContactSurname' && Quote.isEmptyNullOrSpace(item.value)) ||
                (item.name === 'PickUpContactMobile' && Quote.isEmptyNullOrSpace(item.value))) {
                console.log(item.name, item.value)
                return false;
            }
        });

        return result;

    };

    Quote.isEmptyNullOrSpace = function(value) {
        return value === null || value === undefined || value.trim() === "";
    };

// call load existing quote to form fields
    function retrieveExistingQuote(quoteID) {
        // retrieve existing quote according to selected acccusno
        var AccCusNo = $('.js-getDetailsFrom').val();
        Quote.form.find('.js-quotelocationsarea,.js-quotevehiclesarea').addClass('loading');
        window.location.href = Quote.getUrlPath('?qid=' + encodeURIComponent(quoteID) + '&AccCusNo=' + AccCusNo);
    };

    Quote.initSuburbAutocomplete = function (els, opts) {
        var defaults = {
            source: Quote.getUrlPath('/suggestSuburbs'),
            minLength: 3,
        };
        Quote.$.extend(defaults, opts);
        els.autocomplete(defaults);
    };

    /**
     * Applies validation rules to vehicle array elements by ensuring they have unique array names.
     */
    Quote.initVehicleValidation = function (vehicleEl) {
        var $ = Quote.$;
        vehicleEl.find('input,select').each(function (index, field) {
            var field = $(field);
            var fieldName = field.prop('name');
            var rules = Quote.vehicleValidationRules[fieldName];
            var newName = fieldName.replace(/([^\]]*)(.*)/, '$1' + Quote.vehicleUniqueCount + '$2');
            field.prop('name', newName);
            if (rules) {
                field.rules('add', rules);
            }
        });
        Quote.vehicleUniqueCount++;
    }


    /**
     * Sync's contact details from the main account fields to the pick up contact details fields based on checkbox selections.
     */
    Quote.syncContactDetails = function () {
        var $ = Quote.$;
        var nameSuffixes = ['Title', 'FirstName', 'Surname', 'Email', 'Phone', 'Mobile'];
        var checked = $('.js-pickupsync-contactdetails').prop('checked');
        $.each(nameSuffixes, function (index, suffix) {
            var sourceName = 'Contact' + suffix;
            var targetName = 'PickUpContact' + suffix;
            var value = checked ? Quote.form.find('[name="' + sourceName + '"]').val() : '';
            Quote.form.find('[name="' + targetName + '"]').val(value);
        });
    };


    /**
     * Sync's contact details from the main contact fields to the pick up & delivery fields based on checkbox selections.
     */
    Quote.syncPickupDetails = function () {
        var $ = Quote.$;
        var nameSuffixes = ['Title', 'FirstName', 'Surname', 'Email', 'Phone', 'Mobile'];
        var checked = $('.js-deliverysync-pickup').prop('checked');
        $.each(nameSuffixes, function (index, suffix) {
            var sourceName = 'PickUpContact' + suffix;
            var targetName = 'DeliveryContact' + suffix;
            var value = checked ? Quote.form.find('[name="' + sourceName + '"]').val() : '';
            Quote.form.find('[name="' + targetName + '"]').val(value);
        });
    };

    /**
     * Sync's contact details from pick up contact fields to the delivery fields based on checkbox selections.
     */
    Quote.syncDetails = function () {
        var $ = Quote.$;
        var nameSuffixes = ['Title', 'FirstName', 'Surname', 'Email', 'Phone', 'Mobile']
        var types = ['PickUp', 'Delivery'];
        $.each(types, function (index, type) {
            var el = Quote.form.find('.js-' + type.toLowerCase() + 'sync');
            var checked = el.prop('checked');
            $.each(nameSuffixes, function (index, suffix) {
                var sourceName = 'Contact' + suffix;
                var targetName = type + sourceName;
                var value = checked ? Quote.form.find('[name="' + sourceName + '"]').val() : '';
                Quote.form.find('[name="' + targetName + '"]').val(value);
            });
        });
    };

    /**
     * Load quote information from API by QuoteID
     */
    Quote.loadQuote = function (QuoteNo) {
    };

    /**
     *   Address functionalities for quote and book
     *
     */
    var saveDefaultAddress = $('.js-save-default-address');
    saveDefaultAddress.on('click', function (e) {
        var saveType = $(this).data('type');
        if (Quote.validate("stepDefaultAddress" + saveType)) {
            if (typeof (Storage) !== "undefined") {
                Quote.setDefaultAddressToStorage(saveType);
                alert("Saved");
            } else {
                alert("Sorry! No Web Storage support..");
            }
        }
    });
// show button when local storage is not empty
    Quote.initStep1LoadAddress = function () {
        var loadPickUpDefaultAddress = $('.js-load-pickup-default-address');
        var loadDeliveryDefaultAddress = $('.js-load-delivery-default-address');
        if (localStorage.getItem("PickUpdefaultAddress") !== null) {
            loadPickUpDefaultAddress.show();
        } else {
            loadPickUpDefaultAddress.hide();
        }
        if (localStorage.getItem("DeliverydefaultAddress") !== null) {
            loadDeliveryDefaultAddress.show();
        } else {
            loadDeliveryDefaultAddress.hide();
        }
        // only show save default address button when text field has value
        $('.js-deliveryaddress1').on("change", function (event) {
            if ($(this).val() == '') {
                $('.js-save-default-address').hide();
            } else {
                $('.js-save-default-address').show();
            }
        });
        $('.js-pickupaddress1').on("change", function (event) {
            if ($(this).val() == '') {
                $('.js-save-default-address').hide();
            } else {
                $('.js-save-default-address').show();
            }
        });
    }
// load address to fields from local storage
    var loadDefaultAddress = $('.js-load-default-address');
    loadDefaultAddress.on('click', function (e) {
        var type = $(this).data('type');
        var step = $(this).data('step');
        var savedAddress = localStorage.getItem(type + "defaultAddress");
        // find field by data type and populate fields;
        Quote.getDefaultAddressToFields(type, step);
    });

// get addresses from API to dropdown
    var getAddressesButton = $('.js-get-from-addresses');
    getAddressesButton.on('click', function (e) {
        e.preventDefault();
        $('.js-spinner').show();
        var isSystem = $(this).data('addresstype');
        Quote.getAddressesForQuotePage(isSystem, $(this));
    });

// calling the controller
    Quote.getAddressesForQuotePage = function (isSystem, clickedElement) {
        var $ = Quote.$;
        $.ajax({
            type: "POST",
            url: Quote.getUrlPath('/getAddressesForQuotePage'),
            data: {
                isSystem: isSystem
            },
            success: function (response) {
                clickedElement.closest('.js-get-from-address-container').next('.js-results').html(response);
                $('.js-spinner').hide();
                var addressesDropDown = $('.js-results .js-quote-address-dropdown');
                if (addressesDropDown.length) {
                    addressesDropDown.on('change', function (e) {
                        //Todo: check if selected address state matched with quote state.
                        Quote.populateInfoToAddressFields(($(this).find(':selected').data('info')), $(this));
                    });
                }
            }
        });
    };
// fill form fields with data from selected address
    Quote.populateInfoToAddressFields = function (jsonInfo, element) {
        if (jsonInfo) {
            // $('#personaldetails input[name=ContactSurname]').val(jsonInfo.ContactName);
            // $('#personaldetails input[name=ContactPhone]').val(jsonInfo.ContactPhone);
            // populate to the pick-up or delivery address if it's a customer address
            if (element.closest('.js-results').data("type") == 'contact') {
                var names = jsonInfo.ContactName.split(" ");
                $('input[name="PickUpContactFirstName"]').val(names[0]);
                $('input[name="PickUpContactSurname"]').val(names[1]);
                $('input[name="PickUpContactEmail"]').val(jsonInfo.Email);
                $('input[name="PickUpContactPhone"]').val(jsonInfo.ContactPhone);
                // $('input[name="PickUpContactMobile"]').val(jsonInfo.ContactPhone);
            } else {
                var selectorPrefix = '.js-' + element.closest('.js-results').data("type");
                if ($(selectorPrefix + 'suburb').val() == jsonInfo.Suburb) {
                    $(selectorPrefix + 'address1').val(jsonInfo.Address);
                    // $(selectorPrefix+'suburb').val(jsonInfo.Suburb);
                    // $(selectorPrefix+'state').val(jsonInfo.State);
                    // $(selectorPrefix+'postcode').val(jsonInfo.Postcode);
                } else {
                    $(selectorPrefix + 'address1').val("");
                    element.prop('selectedIndex', 0);
                    alert("You cannot select a suburb different from the quoted one");
                }
            }

        }
    };
    Quote.setDefaultAddressToStorage = function (saveType) {
        if (Quote.validateStepChange('stepDefaultAddress' + saveType)) {
            var result = {
                "Address1": Quote.form.find('[name="' + saveType + 'Address1"]').val(),
                "Address2": Quote.form.find('[name="' + saveType + 'Address2"]').val(),
                "Suburb": Quote.form.find('[name="' + saveType + 'Suburb"]').val(),
                "State": Quote.form.find('[name="' + saveType + 'State"]').val(),
                "Postcode": Quote.form.find('[name="' + saveType + 'Postcode"]').val()
            };
            localStorage.setItem(saveType + "defaultAddress", JSON.stringify(result));
        }
    };

    Quote.getDefaultAddressToFields = function (saveType, step) {
        var savedAddress = JSON.parse(localStorage.getItem(saveType + "defaultAddress"));
        if (step && step == "1") {
            Quote.form.find('[name="' + saveType + 'Location"]').val(savedAddress.Suburb + ', ' + savedAddress.State + ', ' + savedAddress.Postcode);
        } else {
            Quote.form.find('[name="' + saveType + 'Address1"]').val(savedAddress.Address1);
            Quote.form.find('[name="' + saveType + 'Address2"]').val(savedAddress.Address2);
            Quote.form.find('[name="' + saveType + 'Suburb"]').val(savedAddress.Suburb);
            Quote.form.find('[name="' + saveType + 'State"]').val(savedAddress.State);
            Quote.form.find('[name="' + saveType + 'Postcode"]').val(savedAddress.Postcode);
        }
    };


    /**
     * -----------------------------------------------------------------------------
     * Main
     * -----------------------------------------------------------------------------
     */

//Anon
    (function ($) {

        //Dom ready
        $(function () {

            //Shadowbox
            // Shadowbox.init();

            Quote.init();
        });
    })(jQuery);
}

/*=====================Ceva Corp===========================*/
